/* =================================================================
   WHISP V2 — MAIN JS
   Cart drawer + AJAX cart + quiz + variant picker
   ================================================================= */

(function () {
  'use strict';

  // ====================================================
  // HELPERS
  // ====================================================

  function emitCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    if (!variantId) return;
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] })
    });
    if (!res.ok) {
      let errMsg = 'No pudimos agregar al carrito';
      try { const eb = await res.json(); errMsg = eb.description || eb.message || errMsg; } catch (e) {}
      throw new Error(errMsg);
    }
    return res.json();
  }

  async function changeCart(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    });
    return res.json();
  }

  function formatARS(cents) {
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency', currency: 'ARS',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return '$' + Math.round(cents / 100).toLocaleString('es-AR');
    }
  }

  // ====================================================
  // CART DRAWER
  // ====================================================

  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-drawer-overlay]');
  const cartCountElements = document.querySelectorAll('[data-cart-count]');

  // Countdown state
  let countdownInterval = null;
  let countdownEnd = null;

  function startCountdown() {
    const el = document.querySelector('[data-cart-countdown]');
    if (!el) return;
    // Reset to 15 min
    countdownEnd = Date.now() + 15 * 60 * 1000;
    if (countdownInterval) clearInterval(countdownInterval);
    updateCountdown();
    countdownInterval = setInterval(updateCountdown, 1000);
  }

  function updateCountdown() {
    const el = document.querySelector('[data-cart-countdown]');
    if (!el || !countdownEnd) return;
    const ms = Math.max(0, countdownEnd - Date.now());
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    el.textContent = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
    if (ms <= 0) {
      // Reset countdown silently to keep urgency
      countdownEnd = Date.now() + 15 * 60 * 1000;
    }
  }

  // Viewers count: random fluctuation 18-32
  function startViewersCount() {
    const el = document.querySelector('[data-viewers-count]');
    if (!el) return;
    let count = 18 + Math.floor(Math.random() * 14);
    el.textContent = count;
    setInterval(() => {
      const delta = Math.floor(Math.random() * 5) - 2;
      count = Math.max(12, Math.min(38, count + delta));
      el.textContent = count;
    }, 3500 + Math.random() * 2000);
  }

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    startCountdown();
    // Focus on close button for a11y
    const closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 300);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  function updateCartCount(count) {
    cartCountElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function updateShippingBar(cart) {
    const wrap = document.querySelector('[data-cart-shipping]');
    const fill = document.querySelector('[data-shipping-fill]');
    const text = document.querySelector('[data-shipping-text]');
    const span = text ? text.querySelector('span') : null;

    // El umbral sale del setting de la sección cart-drawer. Antes estaba
    // hardcodeado en "siempre gratis" y el setting no hacía nada (setting
    // fantasma). Con umbral 0 el comportamiento es idéntico al de antes.
    const threshold = wrap ? parseInt(wrap.getAttribute('data-free-shipping-threshold'), 10) || 0 : 0;
    const unlockedText = (wrap && wrap.getAttribute('data-shipping-unlocked-text')) || '¡Envío gratis desbloqueado!';
    const progressText = (wrap && wrap.getAttribute('data-shipping-progress-text')) || 'Te faltan [falta] para el envío gratis';

    const subtotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;

    if (threshold <= 0 || subtotal >= threshold) {
      if (fill) fill.style.width = '100%';
      if (span) span.textContent = unlockedText;
      return;
    }

    const pct = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
    if (fill) fill.style.width = pct.toFixed(1) + '%';
    if (span) span.textContent = progressText.replace('[falta]', formatARS(threshold - subtotal));
  }

  function updateBonus(cart) {
    const bonusEl = document.querySelector('[data-cart-bonus]');
    if (!bonusEl) return;
    // Si tiene 2 o más unidades totales: bonus desbloqueado
    if (cart.item_count >= 2) {
      bonusEl.style.display = 'flex';
    } else {
      bonusEl.style.display = 'none';
    }
  }

  function updateSavings(cart) {
    const savingsRow = document.querySelector('[data-cart-savings]');
    const savingsAmount = document.querySelector('[data-cart-savings-amount]');
    if (!savingsRow) return;

    // Calc total compare_at - total price
    let totalSavings = 0;
    cart.items.forEach(item => {
      if (item.original_line_price && item.final_line_price) {
        totalSavings += (item.original_line_price - item.final_line_price);
      }
      // También: si hay compare_at_price en variant
      if (item.line_level_total_discount) {
        totalSavings += item.line_level_total_discount;
      }
    });

    if (totalSavings > 0) {
      savingsRow.style.display = 'flex';
      if (savingsAmount) savingsAmount.textContent = formatARS(totalSavings);
    } else {
      savingsRow.style.display = 'none';
    }
  }

  function updateInstallments(cart) {
    const insRow = document.querySelector('[data-cart-installments]');
    const insAmount = document.querySelector('[data-cart-installment-amount]');
    if (!insRow) return;
    if (cart.total_price > 0) {
      insRow.style.display = 'block';
      if (insAmount) insAmount.textContent = formatARS(Math.round(cart.total_price / 3));
    } else {
      insRow.style.display = 'none';
    }
  }

  async function refreshDrawer() {
    if (!drawer) return;
    const cart = await fetchCart();
    updateCartCount(cart.item_count);
    updateShippingBar(cart);
    updateBonus(cart);
    updateSavings(cart);
    updateInstallments(cart);

    const body = drawer.querySelector('[data-cart-body]');
    const foot = drawer.querySelector('[data-cart-foot]');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon" aria-hidden="true">🌿</div>
          <p class="cart-drawer__empty-title">Tu carrito está vacío</p>
          <p class="cart-drawer__empty-sub">Agregá Daily Tribiotic y empezá a desinflamarte desde adentro.</p>
          <a href="/#bundles" class="btn btn--primary" data-cart-close style="margin-top:1rem;">Ver planes</a>
        </div>`;
      if (foot) foot.style.display = 'none';
      return;
    }

    if (foot) foot.style.display = '';

    const itemsHtml = cart.items.map((item, idx) => {
      const hasDiscount = item.original_line_price > item.final_line_price;
      return `
        <div class="cart-item" data-line="${idx + 1}">
          <div class="cart-item__img">
            ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_120x$1')}" alt="${item.title}" loading="lazy" width="60" height="60">` : ''}
          </div>
          <div class="cart-item__info">
            <p class="cart-item__name">${item.product_title}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <div class="cart-item__qty">
              <button type="button" data-cart-decrease="${idx + 1}" aria-label="Restar uno">−</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-cart-increase="${idx + 1}" aria-label="Sumar uno">+</button>
            </div>
          </div>
          <div class="cart-item__price-wrap">
            <span class="cart-item__price">${formatARS(item.final_line_price)}</span>
            ${hasDiscount ? `<span class="cart-item__price-old">${formatARS(item.original_line_price)}</span>` : ''}
            <button type="button" class="cart-item__remove" data-cart-remove="${idx + 1}" aria-label="Quitar">Quitar</button>
          </div>
        </div>`;
    }).join('');

    body.innerHTML = itemsHtml;

    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatARS(cart.total_price);
  }

  // Open / close triggers (delegated)
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      openDrawer();
      refreshDrawer();
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // ESC closes drawer
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // Drawer body actions: qty change, remove
  if (drawer) {
    drawer.addEventListener('click', async function (e) {
      const inc = e.target.closest('[data-cart-increase]');
      const dec = e.target.closest('[data-cart-decrease]');
      const rem = e.target.closest('[data-cart-remove]');

      if (rem) {
        e.preventDefault();
        const lineIdx = parseInt(rem.getAttribute('data-cart-remove'), 10);
        try {
          const updated = await changeCart(lineIdx, 0);
          emitCartUpdated(updated);
          refreshDrawer();
        } catch (err) { console.error(err); }
        return;
      }

      if (!inc && !dec) return;
      e.preventDefault();
      const lineIdx = parseInt((inc || dec).getAttribute(inc ? 'data-cart-increase' : 'data-cart-decrease'), 10);
      const cart = await fetchCart();
      const line = cart.items[lineIdx - 1];
      if (!line) return;
      const newQty = inc ? line.quantity + 1 : Math.max(0, line.quantity - 1);

      try {
        const updated = await changeCart(lineIdx, newQty);
        emitCartUpdated(updated);
        refreshDrawer();
      } catch (err) { console.error(err); }
    });
  }

  // Listen for cart updates from anywhere
  document.addEventListener('cart:updated', function (e) {
    if (e.detail) updateCartCount(e.detail.item_count);
  });

  // Initial count load
  fetchCart().then(c => updateCartCount(c.item_count)).catch(() => {});

  // Start viewers count
  startViewersCount();

  // ====================================================
  // ADD TO CART (whisp-tier-bundles + others)
  // ====================================================

  document.addEventListener('click', async function (e) {
    const addBtn = e.target.closest('[data-whisp-tier-add], [data-add-to-cart]');
    if (!addBtn) return;
    e.preventDefault();

    if (addBtn.hasAttribute('aria-busy')) return;

    const variantId = addBtn.getAttribute('data-variant-id');
    if (!variantId) {
      console.warn('[whisp] variant ID missing');
      return;
    }

    addBtn.setAttribute('aria-busy', 'true');
    const txtSpan = addBtn.querySelector('.whisp-tier-card__cta-text, .add-to-cart-text');
    const original = txtSpan ? txtSpan.textContent : addBtn.textContent;
    if (txtSpan) txtSpan.textContent = 'Agregando...';

    try {
      await addToCart(variantId, 1);
      const cart = await fetchCart();
      emitCartUpdated(cart);
      openDrawer();
      refreshDrawer();
      if (txtSpan) txtSpan.textContent = original;
    } catch (err) {
      console.error(err);
      alert(err.message || 'Hubo un problema. Probá de nuevo.');
      if (txtSpan) txtSpan.textContent = original;
    } finally {
      addBtn.removeAttribute('aria-busy');
    }
  });

  // ====================================================
  // QUIZ
  // ====================================================

  const quiz = document.querySelector('[data-quiz]');
  if (quiz) {
    const answers = {};
    quiz.addEventListener('click', function (e) {
      const opt = e.target.closest('[data-quiz-option]');
      if (!opt) return;
      const step = opt.getAttribute('data-step');
      const value = parseInt(opt.getAttribute('data-value'), 10);
      answers[step] = value;
      const nextStep = parseInt(step, 10) + 1;
      const allSteps = quiz.querySelectorAll('[data-quiz-step]');
      const progressBar = quiz.querySelector('[data-quiz-progress]');
      allSteps.forEach(s => s.classList.remove('is-active'));
      if (nextStep <= 3) {
        const next = quiz.querySelector(`[data-quiz-step="${nextStep}"]`);
        if (next) next.classList.add('is-active');
        if (progressBar) progressBar.style.width = (nextStep * 33) + '%';
      } else {
        const result = quiz.querySelector('[data-quiz-step="result"]');
        if (result) result.classList.add('is-active');
        if (progressBar) progressBar.style.width = '100%';
        const total = (answers['1'] || 0) + (answers['2'] || 0) + (answers['3'] || 0);
        const planEl = quiz.querySelector('[data-quiz-result-plan]');
        const priceEl = quiz.querySelector('[data-quiz-result-price]');
        let plan = 'Plan Inicio', price = '$34.990';
        if (total >= 7) { plan = 'Plan Transformación'; price = '$79.990'; }
        else if (total >= 4) { plan = 'Plan Resultados'; price = '$59.990'; }
        if (planEl) planEl.textContent = plan;
        if (priceEl) priceEl.textContent = price;
      }
    });
    const restart = quiz.querySelector('[data-quiz-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        quiz.querySelectorAll('[data-quiz-step]').forEach(s => s.classList.remove('is-active'));
        const f = quiz.querySelector('[data-quiz-step="1"]');
        if (f) f.classList.add('is-active');
        const pb = quiz.querySelector('[data-quiz-progress]');
        if (pb) pb.style.width = '33%';
      });
    }
  }

  // ====================================================
  // FAQ — auto close others
  // ====================================================

  document.querySelectorAll('.faq-item').forEach(item => {
    item.addEventListener('toggle', function () {
      if (item.open) {
        document.querySelectorAll('.faq-item[open]').forEach(other => {
          if (other !== item) other.removeAttribute('open');
        });
      }
    });
  });

  // ====================================================
  // PRODUCT VARIANT PICKER
  // ====================================================

  document.querySelectorAll('[data-variant-picker]').forEach(picker => {
    picker.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-variant]');
      if (!btn) return;
      picker.querySelectorAll('[data-variant]').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const variantId = btn.getAttribute('data-variant');
      const price = btn.getAttribute('data-price');
      const priceOld = btn.getAttribute('data-price-old');
      const priceEl = document.querySelector('[data-product-price]');
      const priceOldEl = document.querySelector('[data-product-price-old]');
      const addBtn = document.querySelector('[data-add-to-cart]');
      if (priceEl && price) priceEl.textContent = price;
      if (priceOldEl && priceOld) priceOldEl.textContent = priceOld;
      if (addBtn && variantId) addBtn.setAttribute('data-variant-id', variantId);
    });
  });

})();
